import { useState, type FormEvent } from "react";
import { formatDate } from "../../api/dates";
import { liveStops, useCancelStop, useMoveStop, type PlanDay, type PlanDetail, type PlanStop } from "../../api/routing";
import SwapStop from "./SwapStop";

type Change = "move" | "swap" | "off";

/** "Day 2 · 13 Oct 2026 · Skakuza": a day as the change forms name it. */
export function dayLabel(day: PlanDay): string {
  return [`Day ${day.day_index + 1}`, day.date ? formatDate(day.date) : null, day.provider?.name ?? null]
    .filter(Boolean)
    .join(" · ");
}

/** Where on a day a stop goes: before one of its stops still to be driven, or
 * at the end. `leaving` is a stop being moved, never offered as its own place. */
export function PositionField({
  day,
  value,
  onChange,
  leaving,
}: {
  day: PlanDay | undefined;
  value: string;
  onChange: (value: string) => void;
  leaving?: number;
}) {
  return (
    <label className="field">
      Position
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">End of the day</option>
        {(day ? liveStops(day) : [])
          .filter((stop) => stop.id !== leaving)
          .map((stop) => (
            <option key={stop.id} value={stop.id}>
              Before {stop.seq}. {stop.site?.name ?? "—"}
            </option>
          ))}
      </select>
    </label>
  );
}

/** What the office can change about one pending stop when the City asks: move
 * it to another day of the plan, swap it for another site in its cluster (the
 * priority swap), or take it off the plan -- cancelled, and not owed a visit.
 * Every change keeps its reason on the stop and in the site's history. */
export default function ChangeStop({
  detail,
  stop,
  dayIndex,
  onDone,
}: {
  detail: PlanDetail;
  stop: PlanStop;
  dayIndex: number;
  onDone: () => void;
}) {
  const planId = detail.plan.id;
  const move = useMoveStop(planId);
  const cancel = useCancelStop(planId);
  const [change, setChange] = useState<Change>("move");
  const [target, setTarget] = useState("");
  const [before, setBefore] = useState("");
  const [reason, setReason] = useState("");
  // A day already marked complete takes no more stops.
  const days = detail.days.filter((day) => day.day_index !== dayIndex && day.completed_at === null);
  const targetDay = detail.days.find((day) => String(day.day_index) === target);
  const pending = move.isPending || cancel.isPending;
  const why = reason.trim();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (change === "move") {
      move.mutate(
        { stopId: stop.id, day_index: Number(target), before_stop_id: before === "" ? null : Number(before), reason: why },
        { onSuccess: onDone },
      );
    } else if (change === "off") {
      cancel.mutate({ stopId: stop.id, reason: why }, { onSuccess: onDone });
    }
  }

  return (
    <div className="plan-change">
      <div className="segmented" role="radiogroup" aria-label={`Change stop ${stop.seq}`}>
        {(
          [
            ["move", "Move to another day"],
            ["swap", "Swap for another site"],
            ["off", "Take it off the plan"],
          ] as const
        ).map(([value, label]) => (
          <button key={value} type="button" role="radio" aria-checked={change === value} onClick={() => setChange(value)}>
            {label}
          </button>
        ))}
      </div>
      {change === "swap" ? (
        <SwapStop planId={planId} stop={stop} onDone={onDone} />
      ) : (
        <form className="plan-stop-form" onSubmit={submit} aria-label={change === "move" ? `Move stop ${stop.seq}` : `Take stop ${stop.seq} off`}>
          {change === "move" ? (
            <>
              <label className="field">
                Move to
                <select
                  value={target}
                  onChange={(e) => {
                    setTarget(e.target.value);
                    setBefore("");
                  }}
                  required
                >
                  <option value="">Choose the day…</option>
                  {days.map((day) => (
                    <option key={day.day_index} value={day.day_index}>
                      {dayLabel(day)}
                    </option>
                  ))}
                </select>
              </label>
              <PositionField day={targetDay} value={before} onChange={setBefore} leaving={stop.id} />
            </>
          ) : (
            <p className="plan-change-note">Taken off at the City's request: the site is not owed a visit.</p>
          )}
          <label className="field">
            Why
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required />
          </label>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={pending || why === "" || (change === "move" && target === "")}
          >
            {change === "move" ? "Move" : "Take it off"}
          </button>
          {change === "move" && days.length === 0 ? (
            <p className="notice warn">Every other day of this plan is marked complete.</p>
          ) : null}
          {move.isError ? <p className="notice bad">{move.error.message}</p> : null}
          {cancel.isError ? <p className="notice bad">{cancel.error.message}</p> : null}
        </form>
      )}
    </div>
  );
}
